import * as Sentry from "@sentry/react"
import { getCsrfToken } from "./csrf-store"
import { ApiRequestError } from "./errors"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"])

const buildHeaders = (
  method: string,
  extra?: Record<string, string>,
): Headers => {
  const headers = new Headers({ "Content-Type": "application/json" })
  if (UNSAFE_METHODS.has(method)) {
    const csrf = getCsrfToken()
    if (csrf) headers.set("X-CSRF-Token", csrf)
  }
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      headers.set(key, value)
    }
  }
  return headers
}

const handleErrorResponse = async (
  response: Response,
  context: {
    method: string
    url: string
    requestBody?: unknown
    apiModule?: string
  },
): Promise<never> => {
  let body
  try {
    body = await response.json()
  } catch (jsonError) {
    Sentry.captureException(jsonError, {
      tags: {
        errorType: "non_json_error_response",
        httpMethod: context.method,
        ...(context.apiModule ? { apiModule: context.apiModule } : {}),
      },
      extra: {
        url: context.url,
        ...(context.requestBody !== undefined
          ? { requestBody: context.requestBody }
          : {}),
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        responseType: response.type,
        originalError: jsonError,
      },
    })

    body = {
      type: "NetworkError",
      message:
        "Ha ocurrido un error de conexión. Por favor, inténtalo de nuevo.",
    }
  }
  throw new ApiRequestError(body, response)
}

class ApiClient {
  async get<TData>(path: string): Promise<TData> {
    const url = `${API_BASE_URL}${path}`
    const response = await fetch(url, {
      method: "GET",
      headers: buildHeaders("GET"),
      credentials: "include",
    })

    if (response.ok) {
      return response.json() as Promise<TData>
    }

    return handleErrorResponse(response, { method: "GET", url })
  }

  async post<TData>(
    path: string,
    json?: unknown,
    options?: { headers?: Record<string, string> },
  ): Promise<TData> {
    const url = `${API_BASE_URL}${path}`
    const response = await fetch(url, {
      method: "POST",
      body: json ? JSON.stringify(json) : undefined,
      headers: buildHeaders("POST", options?.headers),
      credentials: "include",
    })

    if (response.ok) {
      if (response.status === 204) {
        return Promise.resolve(null) as Promise<TData>
      }
      return response.json() as Promise<TData>
    }

    return handleErrorResponse(response, {
      method: "POST",
      url,
      requestBody: json,
    })
  }

  async put<TData>(path: string, json?: unknown): Promise<TData> {
    const url = `${API_BASE_URL}${path}`
    const response = await fetch(url, {
      method: "PUT",
      body: json ? JSON.stringify(json) : undefined,
      headers: buildHeaders("PUT"),
      credentials: "include",
    })

    if (response.ok) {
      if (response.status === 204) {
        return Promise.resolve(null) as Promise<TData>
      }
      return response.json() as Promise<TData>
    }

    return handleErrorResponse(response, {
      method: "PUT",
      url,
      requestBody: json,
    })
  }
}

const apiClient = new ApiClient()

export { apiClient, API_BASE_URL, ApiClient, handleErrorResponse }
